import { Button } from "@dash/form";
import { Spinner } from "@dash/spinner";
import { useTheme } from "@emotion/react";
import { useClientManager } from "@megaron/react-clients";
import type { CommentAttachmentPayloadDto } from "@megaron/comments-contracts";
import type { MessageAttachmentSummaryDto } from "@megaron/support-contracts";
import type { Uuid } from "@megaron/uuid";
import { Document, Page, pdfjs } from "react-pdf";
import { MdDownload } from "react-icons/md";
import { useLayoutEffect, useMemo, useRef, useState } from "react";

import { decodeBase64ToUint8Array } from "./supportTicketAttachmentDecode";
import { triggerAttachmentDownload } from "./supportTicketAttachmentTriggerDownload";
import { useSupportAttachmentImagePreview } from "./useSupportAttachmentImagePreview";

if (typeof window !== "undefined") {
  pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
}

type Props = {
  commentId: Uuid;
  summary: MessageAttachmentSummaryDto;
};

export function SupportTicketCommentAttachmentExpanded({ commentId, summary }: Props) {
  const theme = useTheme();
  const attachmentQuery = useClientManager("comments").getCommentAttachment().useQuery({
    commentId,
    attachmentId: summary.id,
  });

  const payload: CommentAttachmentPayloadDto | undefined = attachmentQuery.data;
  const displayType = payload?.fileType ?? summary.fileType;
  const { imageSrc, imageError } = useSupportAttachmentImagePreview(payload, displayType);

  const contentRef = useRef<HTMLDivElement>(null);
  const [pageWidth, setPageWidth] = useState(400);

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return undefined;
    const sync = () => {
      const w = el.clientWidth;
      setPageWidth(Math.max(160, Math.floor(w) - 2));
    };
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const pdfBytes = useMemo(() => {
    if (!payload || displayType !== "application/pdf") return null;
    return decodeBase64ToUint8Array(payload.contentBase64);
  }, [payload, displayType]);

  const isImage = displayType.startsWith("image/");
  const isPdf = displayType === "application/pdf";

  return (
    <div css={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, minWidth: 0, width: "100%", maxHeight: "min(78vh, 820px)" }}>
      <div ref={contentRef} css={{ overflowX: "hidden", overflowY: "auto", flex: 1, minHeight: 0, minWidth: 0, width: "100%", boxSizing: "border-box" }}>
        {payload && (
          <div css={{ marginBottom: "0.75rem" }}>
            <Button
              variant="outline"
              size="small"
              icon={<MdDownload />}
              onClick={() => triggerAttachmentDownload(payload.contentBase64, payload.fileType, payload.fileName)}
              css={{ alignSelf: "flex-start" }}
            >
              Pobierz
            </Button>
          </div>
        )}
        {attachmentQuery.isLoading && (
          <div css={{ display: "flex", justifyContent: "center", padding: "3rem" }}>
            <Spinner color={theme.colors.primary} size="32px" />
          </div>
        )}
        {attachmentQuery.isError && (
          <div css={{ color: theme.colors.danger, padding: "1rem" }}>Nie udało się wczytać pliku.</div>
        )}
        {!attachmentQuery.isLoading && isPdf && pdfBytes && (
          <div css={{ display: "flex", justifyContent: "center", width: "100%", minWidth: 0 }}>
            <Document
              file={{ data: pdfBytes }}
              loading={
                <div css={{ padding: "2rem" }}>
                  <Spinner color={theme.colors.primary} size="28px" />
                </div>
              }
            >
              <Page pageNumber={1} width={pageWidth} renderTextLayer={false} renderAnnotationLayer={false} />
            </Document>
          </div>
        )}
        {!attachmentQuery.isLoading && isImage && imageError && (
          <div css={{ color: theme.colors.danger, padding: "1rem" }}>{imageError}</div>
        )}
        {!attachmentQuery.isLoading && isImage && imageSrc && (
          <img alt="" src={imageSrc} css={{ display: "block", maxWidth: "100%", height: "auto", margin: "0 auto" }} />
        )}
      </div>
    </div>
  );
}
